import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { FieldAutofillHistory, useFieldAutofillHistory } from 'api/datamaid';
import {
  DataMaidEntityMenu,
  DataMaidReviewAutofillCarouselModal,
} from 'components/entityMenu/datamaidMenu';
import { useModal } from 'components/modal';
import React, { useMemo, useState } from 'react';

export default function FieldAutofillHistoryPage() {
  const [selectedTab, setSelectedTab] = useState(0);
  const { data, error, isLoading } = useFieldAutofillHistory();
  const { showModal } = useModal();

  // Filter items that need review
  const reviewItems = useMemo(() => {
    if (!data) return [];
    return data.filter((item) => item.status === 'review');
  }, [data]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography color="error">
          Error loading autofill history: {error.message}
        </Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box>
        <Typography>No autofill history found</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Tabs
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
        sx={{ mb: 2 }}
      >
        <Tab label="Needs Review" />
        <Tab label="History" />
      </Tabs>

      {selectedTab === 0 && (
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h5">Fields Awaiting Review</Typography>
            {reviewItems.length > 0 && (
              <Button
                variant="contained"
                onClick={() => {
                  showModal(
                    'Review All DataMaid Field Autofill Changes',
                    <DataMaidReviewAutofillCarouselModal
                      reviewItems={reviewItems}
                    />,
                  );
                }}
              >
                Walk Through All Changes ({reviewItems.length})
              </Button>
            )}
          </Box>

          {reviewItems.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography>No items need review</Typography>
            </Paper>
          ) : (
            <Paper variant="outlined">
              <List>
                {reviewItems.map((item, index) => (
                  <React.Fragment key={item.id}>
                    {index > 0 && <Divider />}
                    <ListItem alignItems="flex-start">
                      <ListItemText
                        primary={
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography variant="subtitle1">
                              Entity ID: {item.entity_id}
                            </Typography>
                            <StatusChip status={item.status} />
                          </Box>
                        }
                        secondary={
                          <Box mt={1}>
                            <Typography variant="body2" component="div">
                              Created: {item.created_at}
                            </Typography>
                            {item.processed_at && (
                              <Typography variant="body2" component="div">
                                Processed: {item.processed_at}
                              </Typography>
                            )}
                            <Typography
                              variant="body2"
                              component="div"
                              mt={1}
                              fontWeight="bold"
                            >
                              New Fields:
                            </Typography>
                            <pre
                              style={{
                                background: 'black',
                                padding: '8px',
                                borderRadius: '4px',
                                fontSize: '14px',
                                overflow: 'auto',
                                maxHeight: '300px',
                              }}
                            >
                              {JSON.stringify(item.new_fields, null, 2)}
                            </pre>

                            <Box mt={2}>
                              <Typography
                                variant="body2"
                                component="div"
                                fontWeight="bold"
                              >
                                Actions:
                              </Typography>
                              <DataMaidItemActions item={item} />
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      )}

      {selectedTab === 1 && (
        <Box>
          <Typography variant="h5" gutterBottom>
            Field Autofill History
          </Typography>

          <Paper variant="outlined">
            <List>
              {data.map((item, index) => (
                <React.Fragment key={item.id}>
                  {index > 0 && <Divider />}
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="subtitle1">
                            Entity ID: {item.entity_id}
                          </Typography>
                          <StatusChip status={item.status} />
                        </Box>
                      }
                      secondary={
                        <Box mt={1}>
                          <Typography variant="body2" component="div">
                            Created: {item.created_at}
                          </Typography>
                          {item.processed_at && (
                            <Typography variant="body2" component="div">
                              Processed: {item.processed_at}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            component="div"
                            mt={1}
                            fontWeight="bold"
                          >
                            New Fields:
                          </Typography>
                          <pre
                            style={{
                              background: 'black',
                              padding: '8px',
                              borderRadius: '4px',
                              fontSize: '14px',
                              overflow: 'auto',
                              maxHeight: '300px',
                            }}
                          >
                            {JSON.stringify(item.new_fields, null, 2)}
                          </pre>
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>
      )}
    </Box>
  );
}

function StatusChip({ status }: { status: FieldAutofillHistory['status'] }) {
  let color:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning' = 'default';

  switch (status) {
    case 'pending':
      color = 'warning';
      break;
    case 'processing':
      color = 'info';
      break;
    case 'review':
      color = 'secondary';
      break;
    case 'failed':
      color = 'error';
      break;
    case 'completed':
      color = 'success';
      break;
  }

  return <Chip size="small" label={status} color={color} />;
}

function DataMaidItemActions({ item }: { item: FieldAutofillHistory }) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <Box>
      <ListItemButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
      >
        <ListItemText primary="Review Changes" />
      </ListItemButton>
      <DataMaidEntityMenu
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        entities={[item.entity_id]}
      />
    </Box>
  );
}
